import { getNetworkTopologyMapConfig } from 'data/network-topology/network-topology.adapter';
import L, { Point } from 'leaflet';

const mapConfig = getNetworkTopologyMapConfig();
const maxZoomLevel = mapConfig.mapPosition.maxZoom;
export function determineLineType(horizontalDistance: number, verticalDistance: number, hDist: number, zoom: number, maxZoomLevel: number) {
    let isVerticalLine = false;
    let isCurvedVerticalLine = false;
    if (horizontalDistance > 30 && verticalDistance < 25) {
        isVerticalLine = false;
    } else if ((hDist > -30 && hDist < 30 && verticalDistance > 20) ||
        (zoom > maxZoomLevel - 2 && hDist > -30 && hDist < 30 && verticalDistance > 100)) {
        isCurvedVerticalLine = true;
        isVerticalLine = false;
    } else if ((horizontalDistance < 100 && horizontalDistance > 40 && zoom >= 5 && zoom < maxZoomLevel) ||
        (horizontalDistance < 40 && horizontalDistance > 25 && verticalDistance > 20) ||
        (horizontalDistance < 300 && zoom === maxZoomLevel)) {
        // CONTROL_POINTS_RADIUS = 0.3;
        isCurvedVerticalLine = false;
        isVerticalLine = true;
    } else if (horizontalDistance > 40 && verticalDistance < 30) {
        isCurvedVerticalLine = false;
        isVerticalLine = false;
    }
    return { isVerticalLine, isCurvedVerticalLine };
}

export const getCenters = (startPoint: Point, endPoint: Point) => {
    const horizontalCenter = Math.abs(((startPoint.y - endPoint.y) / 2));
    const verticalCenter = Math.abs(((startPoint.x - endPoint.x) / 2));
    return {
        horizontalCenter, verticalCenter
    };
}

export const getDistances = (map: L.Map, startPoint: Point, endPoint: Point) => {
    const horizontalDistance = Math.abs(Math.abs(map.latLngToContainerPoint(new L.LatLng(startPoint.x, startPoint.y)).x) - Math.abs(map.latLngToContainerPoint(new L.LatLng(endPoint.x, endPoint.y)).x));
    const hDist = map.latLngToContainerPoint(new L.LatLng(startPoint.x, startPoint.y)).x - map.latLngToContainerPoint(new L.LatLng(endPoint.x, endPoint.y)).x;
    const verticalDistance = Math.abs(Math.abs(map.latLngToContainerPoint(new L.LatLng(startPoint.x, startPoint.y)).y) - Math.abs(map.latLngToContainerPoint(new L.LatLng(endPoint.x, endPoint.y)).y));
    return {
        horizontalDistance, hDist, verticalDistance
    };
}

export const calculateDeltas = (startPoint: Point, endPoint: Point): {
    dx: number;
    dy: number;
    absDx: number;
    absDy: number;
} => {
    const dx = Math.abs(endPoint.x) - Math.abs(startPoint.x);
    const dy = Math.abs(endPoint.y) - Math.abs(startPoint.y);
    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);
    return {
        dx, dy, absDx, absDy
    };
};

export const calculateControlPoints = ({
    startPoint,
    endPoint,
    map,
    CONTROL_POINTS_RADIUS,
    isVerticalLine,
    isCurvedVerticalLine,
    horizontalCenter,
    verticalCenter,
    type
}: {
    startPoint: L.Point,
    endPoint: L.Point,
    map: L.Map,
    CONTROL_POINTS_RADIUS: number,
    isVerticalLine: boolean,
    isCurvedVerticalLine: boolean,
    horizontalCenter: number,
    verticalCenter: number,
    type: string
}): {
    S: Point;
    L1: Point;
    Q1: Point;
    L2: Point;
    L3: Point;
    Q2: Point;
    L4: Point;
    E: Point;
} => {
    const zoom = map.getZoom();
    const {
        absDx, absDy, dx, dy
       } = calculateDeltas(startPoint, endPoint);
    let finalStartPoint = new L.LatLng(startPoint.x, startPoint.y);
    let finalEndPoint = new L.LatLng(endPoint.x, endPoint.y);
    let iconWidth = 30;
    if (absDx < 0.4) {
        CONTROL_POINTS_RADIUS = 0;
    }
    const origIconWidth = iconWidth;
    if (iconWidth && (type === 'af' || type === 'afCluster')) {
        iconWidth /= 2;
        if (!isVerticalLine && !isCurvedVerticalLine) {
            const icon = (dy > 0) ? -iconWidth : iconWidth;
            const startContainerPoint = map.latLngToContainerPoint(new L.LatLng(startPoint.x, startPoint.y));
            const startContainerPointWithIcon = new L.Point(startContainerPoint.x + icon, startContainerPoint.y);
            finalStartPoint = map.containerPointToLatLng(startContainerPointWithIcon);

            const xcipioIcon = (dy > 0) ? -iconWidth : iconWidth;
            const endContainerPoint = map.latLngToContainerPoint(new L.LatLng(endPoint.x, endPoint.y));
            const endContainerPointWithIcon = new L.Point(endContainerPoint.x - xcipioIcon, endContainerPoint.y);
            finalEndPoint = map.containerPointToLatLng(endContainerPointWithIcon);
            horizontalCenter = Math.abs(((finalStartPoint.lng - finalEndPoint.lng) / 2));
        } else if (isCurvedVerticalLine) {
            const icon = (dx > 0) ? -iconWidth : iconWidth;
            const startContainerPoint = map.latLngToContainerPoint(new L.LatLng(startPoint.x, startPoint.y));
            const startContainerPointWithIcon = new L.Point(startContainerPoint.x, startContainerPoint.y + icon);
            finalStartPoint = map.containerPointToLatLng(startContainerPointWithIcon);

            const xcipioIcon = (dx > 0) ? -iconWidth : iconWidth;
            const endContainerPoint = map.latLngToContainerPoint(new L.LatLng(endPoint.x, endPoint.y));
            const endContainerPointWithIcon = new L.Point(endContainerPoint.x, endContainerPoint.y - xcipioIcon);
            finalEndPoint = map.containerPointToLatLng(endContainerPointWithIcon);
            horizontalCenter = 0;
            verticalCenter = Math.abs(((finalStartPoint.lat - finalEndPoint.lat) / 2));
        } else {
            const icon = (dy > 0) ? -iconWidth : iconWidth;
            const startContainerPoint = map.latLngToContainerPoint(new L.LatLng(startPoint.x, startPoint.y));
            const startContainerPointWithIcon = new L.Point(startContainerPoint.x + icon, startContainerPoint.y);
            finalStartPoint = map.containerPointToLatLng(startContainerPointWithIcon);

            const xcipioIcon = (dx > 0) ? -iconWidth : iconWidth;
            const endContainerPoint = map.latLngToContainerPoint(new L.LatLng(endPoint.x, endPoint.y));
            const endContainerPointWithIcon = new L.Point(endContainerPoint.x, endContainerPoint.y - xcipioIcon);
            finalEndPoint = map.containerPointToLatLng(endContainerPointWithIcon);
            horizontalCenter = 0;
        }
    }
    if (iconWidth && (type === 'cf' || type === 'cfCluster')) {
        iconWidth /= 2;
        if (!isVerticalLine && !isCurvedVerticalLine) {
            const xcipioIcon = (dy > 0) ? -iconWidth : iconWidth;
            const startContainerPoint = map.latLngToContainerPoint(new L.LatLng(startPoint.x, startPoint.y));
            const startContainerPointWithIcon = new L.Point(startContainerPoint.x + xcipioIcon, startContainerPoint.y);
            finalStartPoint = map.containerPointToLatLng(startContainerPointWithIcon);

            const icon = (dy > 0) ? -iconWidth : iconWidth;
            const endContainerPoint = map.latLngToContainerPoint(new L.LatLng(endPoint.x, endPoint.y));
            const endContainerPointWithIcon = new L.Point(endContainerPoint.x - icon, endContainerPoint.y);
            finalEndPoint = map.containerPointToLatLng(endContainerPointWithIcon);
            horizontalCenter = Math.abs(((finalStartPoint.lng - finalEndPoint.lng) / 2));
        } else if (isCurvedVerticalLine) {
            const xcipioIcon = (dx > 0) ? iconWidth : -iconWidth;
            const startContainerPoint = map.latLngToContainerPoint(new L.LatLng(startPoint.x, startPoint.y));
            const startContainerPointWithIcon = new L.Point(startContainerPoint.x, startContainerPoint.y - xcipioIcon);
            finalStartPoint = map.containerPointToLatLng(startContainerPointWithIcon);

            const iconLat = (dx > 0) ? (iconWidth) : -(iconWidth);
            const endContainerPoint = map.latLngToContainerPoint(new L.LatLng(endPoint.x, endPoint.y));
            const endContainerPointWithIcon = new L.Point(endContainerPoint.x, endContainerPoint.y + iconLat);
            finalEndPoint = map.containerPointToLatLng(endContainerPointWithIcon);
            horizontalCenter = 0;
        } else {
            const xcipioIcon = (dy > 0) ? iconWidth : -iconWidth;
            const startContainerPoint = map.latLngToContainerPoint(new L.LatLng(startPoint.x, startPoint.y));
            const startContainerPointWithIcon = new L.Point(startContainerPoint.x - xcipioIcon, startContainerPoint.y);
            finalStartPoint = map.containerPointToLatLng(startContainerPointWithIcon);

            const icon = (dx > 0) ? iconWidth : -iconWidth;
            const endContainerPoint = map.latLngToContainerPoint(new L.LatLng(endPoint.x, endPoint.y));
            const endContainerPointWithIcon = new L.Point(endContainerPoint.x, endContainerPoint.y + icon);
            finalEndPoint = map.containerPointToLatLng(endContainerPointWithIcon);
            horizontalCenter = 0;
        }
    }
    let fixedLineInflectionConstant = horizontalCenter - CONTROL_POINTS_RADIUS;
    if (dy > 0) {
        fixedLineInflectionConstant = -fixedLineInflectionConstant;
    }
    let centerDistance = (dy > 0) ? -horizontalCenter : horizontalCenter;
    let controlPointL2xAdjust = (dx < 0) ? -CONTROL_POINTS_RADIUS : CONTROL_POINTS_RADIUS;
    const controlPointL2yAdjust = (dy > 0) ? -horizontalCenter : horizontalCenter;

    let controlPointL3xAdjust = (dx < 0) ? CONTROL_POINTS_RADIUS : -CONTROL_POINTS_RADIUS;
    let controlPointL4yAdjust = (dy > 0) ? -(horizontalCenter + CONTROL_POINTS_RADIUS) : (horizontalCenter + CONTROL_POINTS_RADIUS);
    if (isCurvedVerticalLine) {
        fixedLineInflectionConstant = verticalCenter - CONTROL_POINTS_RADIUS;
        if (dx < 0) {
            fixedLineInflectionConstant = -fixedLineInflectionConstant;
        }
        centerDistance = (dx < 0) ? -verticalCenter : verticalCenter;
        controlPointL2xAdjust = (dy > 0) ? -CONTROL_POINTS_RADIUS : CONTROL_POINTS_RADIUS;
        controlPointL3xAdjust = (dy < 0) ? -CONTROL_POINTS_RADIUS : CONTROL_POINTS_RADIUS;
        controlPointL4yAdjust = (dx < 0) ? -CONTROL_POINTS_RADIUS : CONTROL_POINTS_RADIUS;
    }
    const S = {
        x: finalStartPoint.lat,
        y: finalStartPoint.lng
    } as Point;
    // const L1 = !isVerticalLine ? {
    //   x: S.x,
    //   y: S.y + fixedLineInflectionConstant
    // } as Point : {
    //   x: S.x,
    //   y: finalEndPoint.lng + fixedLineInflectionConstant
    // } as Point;
    let L1 = {
        x: S.x,
        y: S.y + fixedLineInflectionConstant
    } as Point;
    if (isCurvedVerticalLine) {
        L1 = {
            x: S.x + fixedLineInflectionConstant,
            y: S.y
        } as Point
    } else if (isVerticalLine) {
        L1 = {
            x: S.x,
            y: finalEndPoint.lng + fixedLineInflectionConstant
        } as Point
    }
    // const Q1 = !isVerticalLine ? {
    //   x: S.x,
    //   y: S.y + centerDistance
    // } as Point : {
    //   x: S.x,
    //   y: finalEndPoint.lng
    // } as Point;
    let Q1 = {
        x: S.x,
        y: S.y + centerDistance
    } as Point;
    if (isCurvedVerticalLine) {
        Q1 = {
            x: S.x + centerDistance,
            y: S.y
        } as Point;
    } else if (isVerticalLine) {
        Q1 = {
            x: S.x,
            y: finalEndPoint.lng
        } as Point;
    }
    // const L2 = !isVerticalLine ? {
    //   x: S.x + controlPointL2xAdjust,
    //   y: S.y + controlPointL2yAdjust
    // } as Point : {
    //   x: S.x + controlPointL2xAdjust,
    //   y: finalEndPoint.lng
    // } as Point;
    let L2 = {
        x: S.x + controlPointL2xAdjust,
        y: S.y + controlPointL2yAdjust
    } as Point;
    if (isCurvedVerticalLine) {
        L2 = {
            x: S.x + centerDistance,
            y: S.y + controlPointL2xAdjust
        } as Point;
    } else if (isVerticalLine) {
        L2 = {
            x: S.x + controlPointL2xAdjust,
            y: finalEndPoint.lng
        } as Point;
    }

    // const L3 = {
    //   x: finalEndPoint.lat + controlPointL3xAdjust,
    //   y: S.y + controlPointL2yAdjust
    // } as Point;
    // const Q2 = {
    //   x: finalEndPoint.lat,
    //   y: S.y + centerDistance
    // } as Point;
    let L3 = {
        x: finalEndPoint.lat + controlPointL3xAdjust,
        y: S.y + controlPointL2yAdjust
    } as Point;
    if (isCurvedVerticalLine) {
        L3 = {
            x: S.x + centerDistance,
            y: finalEndPoint.lng + controlPointL3xAdjust
        } as Point;
    }
    let Q2 = {
        x: finalEndPoint.lat,
        y: S.y + centerDistance
    } as Point;
    if (isCurvedVerticalLine) {
        Q2 = {
            x: S.x + centerDistance,
            y: finalEndPoint.lng
        } as Point;
    }

    let L4 = {
        x: finalEndPoint.lat,
        y: S.y + controlPointL4yAdjust
    } as Point;
    if (isCurvedVerticalLine) {
        L4 = {
            x: Q2.x + controlPointL4yAdjust,
            y: Q2.y
        } as Point;
    }

    const E = {
        x: finalEndPoint.lat,
        y: finalEndPoint.lng
    } as Point;

    if (dx < 0) {
        if (L2.x < L3.x) {
            const temp = L2.x;
            L2.x = L3.x;
            L3.x = temp;
        }
    } else {
        if (L2.x > finalEndPoint.lat) {
            L2.x = finalEndPoint.lat;
        }
        if (L2.x > L3.x) {
            const temp = L2.x;
            L2.x = L3.x;
            L3.x = temp;
        }
    }

    return {
        S,
        L1,
        Q1,
        L2,
        L3,
        Q2,
        L4,
        E
    };
};
